import { Box, Button, styled } from '@mui/material';

import {
  EFocusView,
  GeoMap,
  LegendBar,
  Overlay,
  useAppDispatch,
  WebGLHeatMapLayer,
  widgetInstanceActions,
  WindDataProvider,
  WindThemeProvider,
  WindThemeToggler,
  WindTimeBar,
  type GeoMapProps,
  type WidgetConfigBase,
  type WidgetProps,
} from '#features';
import { LegendToggleIcon } from '#shared';

import { Widget } from '../base';

type Settings = {
  initialFocus: GeoMapProps['initialFocus'];
  legendExtraEnabled: boolean;
  heatMapChangeTimeout: number;
};

const config: WidgetConfigBase<Settings> = {
  initialFocus: {
    type: 'select',
    label: 'Initial Focus',
    defaultValue: EFocusView.US_CONTINENTAL,
    options: Object.values(EFocusView).map(view => ({
      label: view,
      value: view as string,
    })),
  },
  legendExtraEnabled: {
    type: 'boolean',
    label: 'Show Extra Legend',
    defaultValue: true,
  },
  heatMapChangeTimeout: {
    type: 'number',
    label: 'Heatmap Change Timeout (seconds)',
    defaultValue: 1,
    min: 0.1,
    step: 0.1,
  },
};

export interface WidgetWindMapExternalProps
  extends Partial<GeoMapProps>, Partial<Settings> {
  showSettingsButton?: boolean;
  isStandalone?: boolean;
  slots?: {
    legendBar?: boolean;
    timeBar?: boolean;
    overlay?: boolean;
    heatMapLayer?: boolean;
  };
  mapPadding?: GeoMapProps['padding'];
  /** Render as preview with image instead of actual map component */
  preview?: boolean;
}

const PREFIX = 'TSMED-WidgetWindMap';
export const widgetWindMapClasses = {
  root: `${PREFIX}-root`,
};

const ToggleLegendStyled = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  zIndex: 1000,
  left: 190,
  backgroundColor: theme.palette.mapBlue[700],
  border: `2px solid ${theme.palette.mapBlue[600]}`,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[3],
  padding: 0,
  minWidth: theme.spacing(5),
  color: theme.palette.common.white,
  width: theme.spacing(5),
  height: theme.spacing(5),
  overflow: 'hidden',
  '& svg': { fill: theme.palette.common.white },
  '&:hover': { backgroundColor: theme.palette.mapBlue[800] },
}));

const Root = styled(Widget, {
  name: PREFIX,
})(({ theme: { spacing } }) => ({
  [`&.${widgetWindMapClasses.root}`]: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    // Always fill the slot the layout gives us. Layout slots are always
    // definite-height (Region grid stretch -> Section flex -> Block 100%), so a
    // width-derived `minHeight` (calc(100cqw / 18 * 9)) is unnecessary and
    // actively harmful: `cqw` resolves against the full center section width, so
    // when the center is split between the wind map and another widget the floor
    // exceeds the shrunken slot height. The Root then overflows its
    // `overflow: hidden` slot and the bottom-pinned LegendBar gets clipped out of
    // view (TSMED-1408). Filling the slot keeps the legend inside the visible
    // region instead of overflowing the clipped area.
    height: '100%',
    minHeight: 0,
    flexGrow: 1,
    borderRadius: spacing(2),
    overflow: 'hidden',
  },
}));

export const WidgetWindMap = ({
  // Widget plumbing
  instance,
  showSettingsButton,
  className,
  // External/override props
  slots,
  mapPadding,
  preview = false,
  // `isStandalone` is still accepted for API compatibility (passed by the
  // standalone WindMapPage frames) but no longer affects sizing — the widget
  // always fills its slot (see Root above) — so it is intentionally ignored.
  ...rest
}: WidgetProps<Settings> & WidgetWindMapExternalProps) => {
  const dispatch = useAppDispatch();
  const slotsWithDefaults = {
    legendBar: true,
    timeBar: true,
    overlay: true,
    heatMapLayer: true,
    ...slots,
  };
  const showSettings = showSettingsButton ?? true;

  const defaults = Object.keys(config).reduce<Partial<Settings>>((acc, key) => {
    if (rest[key] !== undefined) {
      acc[key] = rest[key];
    }
    return acc;
  }, {});
  return (
    <Root
      className={widgetWindMapClasses.root}
      config={config}
      instance={instance}
      showSettingsButton={showSettings}
      {...defaults}
    >
      {/* TODO: Solve typing issue */}

      {resolved => {
        const settings = resolved as Settings;

        const heatMapChangeTimeout = settings.heatMapChangeTimeout ?? 1;

        // Preview mode: render image instead of actual map
        if (preview) {
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src="/windmap-long.svg"
                alt="Wind Map"
                style={{
                  borderRadius: 'inherit',
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          );
        }

        return (
          <GeoMap
            initialFocus={settings.initialFocus}
            className={className}
            padding={mapPadding}
          >
            <WindThemeProvider>
              <WindDataProvider>
                {slotsWithDefaults.legendBar && (
                  <ToggleLegendStyled
                    onClick={() => {
                      dispatch(
                        widgetInstanceActions.setWidgetInstance({
                          settings: {
                            legendExtraEnabled: !settings.legendExtraEnabled,
                          },
                          id: instance.id,
                        }),
                      );
                    }}
                    title={
                      settings.legendExtraEnabled
                        ? 'Hide Extra Legend'
                        : 'Show Extra Legend'
                    }
                  >
                    <LegendToggleIcon />
                  </ToggleLegendStyled>
                )}

                {slotsWithDefaults.legendBar && <WindThemeToggler />}
                {slotsWithDefaults.heatMapLayer && (
                  <WebGLHeatMapLayer blendDurationSec={heatMapChangeTimeout} />
                )}
                {slotsWithDefaults.overlay && <Overlay />}
                {slotsWithDefaults.timeBar && (
                  <WindTimeBar heatMapChangeTimeout={heatMapChangeTimeout} />
                )}
                {slotsWithDefaults.legendBar && (
                  <LegendBar legendExtraEnabled={settings.legendExtraEnabled} />
                )}
              </WindDataProvider>
            </WindThemeProvider>
          </GeoMap>
        );
      }}
    </Root>
  );
};

WidgetWindMap.config = config;
